import { CallPip } from '@/components/call-bar/call-pip';
import { PipSessionPanel } from '@/components/call-bar/pip-session-panel';
import type { DeclineState } from '@/providers/app-call-actions/app-call-actions-provider';
import {
  getPipSessionData,
  pipEndSession,
} from '@/views/power-dialer/providers/power-dialer-pip-bridge';
import type {
  VoiceCallStateStatus,
  VoiceContact,
  VoiceContactExtra,
  VoiceContactUserLine,
} from '@packages/client-sdk-react/src/voice/voice-call-container';
import { createElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useOngoingCallTags } from './use-ongoing-call-tags';

interface UseCallPictureInPictureOptions {
  userLine?: VoiceContactUserLine;
  extra?: VoiceContactExtra;
  callId?: string;
  callStatus: VoiceCallStateStatus;
  contact?: VoiceContact;
  duration: number;
  isMuted: boolean;
  direction: 'incoming' | 'outgoing' | 'unknown';
  onToggleMute: () => void;
  onAnswer: () => Promise<boolean>;
  onDecline: () => void;
  onHangup: () => void;
  microphoneDeviceId?: string;
  onContactClick?: () => void;
  hasSecondIncomingCall?: boolean;
  declineState?: DeclineState;
  enabled?: boolean;
  /** Called when the user taps "End session" in the PiP panel. */
  onEndSession?: () => void;
}

// Check if running in Electron with PiP support
const hasNativePip = () =>
  typeof window !== 'undefined' && window.desktopAPI?.pipCall !== undefined;

const logPip = (...args: any[]) => {
  if (import.meta.env.DEV) {
    // biome-ignore lint/suspicious/noConsoleLog: for dev
    console.log(...args);
  }
};

const DECLINED_AUTO_HIDE_DELAY = 3000;

export function useCallPictureInPicture({
  callStatus,
  callId,
  contact,
  isMuted,
  duration,
  direction,
  onToggleMute,
  onAnswer,
  onDecline,
  onHangup,
  userLine,
  extra,
  onContactClick,
  hasSecondIncomingCall,
  declineState,
  microphoneDeviceId,
  enabled = true,
  onEndSession,
}: UseCallPictureInPictureOptions) {
  const pipWindowRef = useRef<Window | null>(null);
  const [pipContainer, setPipContainer] = useState<HTMLElement | null>(null);
  const [isNativePipOpen, setIsNativePipOpen] = useState(false);

  const wasOpenedManuallyRef = useRef(false);
  const isCreatingPipRef = useRef(false);

  const [isDeclinedPip, setIsDeclinedPip] = useState(false);
  const declinedSnapshotRef = useRef<{
    contact: typeof contact;
    userLine: typeof userLine;
    extra: typeof extra;
  } | null>(null);

  const isElectron = hasNativePip();

  const { tags: callTagsList, selectedTagKeys, addTag, removeTag } = useOngoingCallTags();

  const callTagsPayload = useMemo(
    () => ({
      tags: callTagsList.map((tag) => ({
        key: tag.key,
        name: tag.name,
        hex_color: tag.hex_color ?? null,
      })),
      selectedTagKeys,
    }),
    [callTagsList, selectedTagKeys],
  );

  // ===== Native Electron PiP =====
  const showNativePip = useCallback(
    (manual = true) => {
      if (!isElectron || !enabled) {
        return;
      }

      wasOpenedManuallyRef.current = manual;

      const session = getPipSessionData();
      window.desktopAPI?.pipCall.show.callback({
        contactName: contact?.name,
        contactPhoneNumber: contact?.phoneNumber,
        contactCompanyName: contact?.companyName,
        contactJobTitle: contact?.jobTitle,
        callId,
        callStatus,
        isMuted,
        duration,
        direction,
        userLine: userLine
          ? {
              lineName: userLine.lineName,
              lineNumber: userLine.lineNumber,
            }
          : undefined,
        extra: extra
          ? {
              summary: extra.summary,
            }
          : undefined,
        microphoneDeviceId,
        declineState,
        session: session ?? null,
        callTags: callTagsPayload,
      });
      setIsNativePipOpen(true);
    },
    [
      isElectron,
      enabled,
      contact,
      callId,
      callStatus,
      isMuted,
      duration,
      direction,
      userLine,
      extra,
      declineState,
      microphoneDeviceId,
      callTagsPayload,
    ],
  );

  const hideNativePip = useCallback(() => {
    if (!isElectron) {
      return;
    }
    window.desktopAPI?.pipCall.hide.callback();
    setIsNativePipOpen(false);
  }, [isElectron]);

  const destroyNativePip = useCallback(() => {
    if (!isElectron) {
      return;
    }
    window.desktopAPI?.pipCall.destroy.callback();
    setIsNativePipOpen(false);
  }, [isElectron]);

  const handleDeclineWithFlag = useCallback(() => {
    declinedSnapshotRef.current = { contact, userLine, extra };
    setIsDeclinedPip(true);
    onDecline();
  }, [onDecline, contact, userLine, extra]);

  const updateNativePip = useCallback(() => {
    if (!isElectron || !enabled || isDeclinedPip) {
      return;
    }

    // Update call data so main process can auto-show PiP on blur
    const isActiveCall =
      callStatus === 'in-progress' ||
      callStatus === 'ringing' ||
      callStatus === 'initiating' ||
      callStatus === 'transferring' ||
      callStatus === 'hangingup' ||
      callStatus === 'reconnectable';

    const session = getPipSessionData();
    const hasActiveSession = session?.isActive === true;

    if (!isActiveCall && !hasActiveSession && !hasSecondIncomingCall) {
      return;
    }

    window.desktopAPI?.pipCall.updateData.callback({
      contactName: contact?.name,
      contactPhoneNumber: contact?.phoneNumber,
      contactCompanyName: contact?.companyName,
      contactJobTitle: contact?.jobTitle,
      callId,
      callStatus,
      isMuted,
      duration,
      direction,
      userLine: userLine
        ? {
            lineName: userLine.lineName,
            lineNumber: userLine.lineNumber,
          }
        : undefined,
      extra: extra
        ? {
            summary: extra.summary,
          }
        : undefined,
      microphoneDeviceId,
      declineState,
      session: session ?? null,
      callTags: callTagsPayload,
    });
  }, [
    isElectron,
    enabled,
    isDeclinedPip,
    contact,
    callId,
    callStatus,
    isMuted,
    duration,
    direction,
    userLine,
    extra,
    declineState,
    microphoneDeviceId,
    callTagsPayload,
    hasSecondIncomingCall,
  ]);

  // Register event listeners for native PiP actions
  useEffect(() => {
    if (!isElectron) {
      return;
    }

    const cleanupToggleMute = window.desktopAPI?.pipCall.onToggleMute.callback(onToggleMute);
    const cleanupAnswer = window.desktopAPI?.pipCall.onAnswer.callback(onAnswer);
    const cleanupDecline = window.desktopAPI?.pipCall.onDecline.callback(handleDeclineWithFlag);
    const cleanupHangup = window.desktopAPI?.pipCall.onHangup.callback(onHangup);
    const cleanupContactClick = window.desktopAPI?.pipCall.onContactClick.callback(() => {
      onContactClick?.();
    });
    const cleanupAddTag = window.desktopAPI?.pipCall.onAddTag.callback((tagKey: string) => {
      addTag(tagKey);
    });
    const cleanupRemoveTag = window.desktopAPI?.pipCall.onRemoveTag.callback((tagKey: string) => {
      removeTag(tagKey);
    });
    const cleanupFocusMain = window.desktopAPI?.pipCall.onFocusMain.callback(() => {
      setIsNativePipOpen(false);
    });
    const cleanupClose = window.desktopAPI?.pipCall.onClose.callback(() => {
      setIsDeclinedPip(false);
      declinedSnapshotRef.current = null;
      setIsNativePipOpen(false);
    });
    const cleanupShow = window.desktopAPI?.pipCall.onShow.callback(() => {
      // PiP was auto-opened (e.g., on blur)
      wasOpenedManuallyRef.current = false;
      setIsNativePipOpen(true);
    });
    const cleanupEndSession = onEndSession
      ? window.desktopAPI?.pipCall.onEndSession.callback(onEndSession)
      : undefined;
    const cleanupMainWindowFocused = window.desktopAPI?.pipCall.onMainWindowFocused.callback(() => {
      // Auto-hide PiP when returning to main window if it wasn't manually opened
      if (!wasOpenedManuallyRef.current && isNativePipOpen) {
        setIsDeclinedPip(false);
        declinedSnapshotRef.current = null;
        hideNativePip();
      }
    });

    return () => {
      cleanupToggleMute?.();
      cleanupAnswer?.();
      cleanupDecline?.();
      cleanupHangup?.();
      cleanupContactClick?.();
      cleanupAddTag?.();
      cleanupRemoveTag?.();
      cleanupFocusMain?.();
      cleanupClose?.();
      cleanupShow?.();
      cleanupEndSession?.();
      cleanupMainWindowFocused?.();
    };
  }, [
    isElectron,
    onToggleMute,
    onAnswer,
    handleDeclineWithFlag,
    onHangup,
    onEndSession,
    onContactClick,
    addTag,
    removeTag,
    isNativePipOpen,
    hideNativePip,
  ]);

  useEffect(() => {
    updateNativePip();
  }, [updateNativePip]);

  useEffect(() => {
    if (!isElectron) {
      return;
    }

    const isActive =
      callStatus === 'in-progress' ||
      callStatus === 'ringing' ||
      callStatus === 'initiating' ||
      callStatus === 'transferring' ||
      callStatus === 'hangingup' ||
      callStatus === 'reconnectable';

    // Don't destroy PiP during a power dialer session — the next call
    // will start shortly and the PiP should stay open.
    const hasActiveSession = !!getPipSessionData()?.isActive;

    if (!isActive && !hasSecondIncomingCall && !isDeclinedPip && !hasActiveSession) {
      destroyNativePip();
    }
  }, [isElectron, callStatus, hasSecondIncomingCall, isDeclinedPip, destroyNativePip]);

  // Auto-show native PiP for incoming calls in Electron
  useEffect(() => {
    if (!isElectron || !enabled) {
      return;
    }

    const isIncomingRinging = callStatus === 'ringing' && direction === 'incoming';
    if (isIncomingRinging) {
      showNativePip(false); // Auto-opened for incoming call
    }
  }, [isElectron, enabled, callStatus, direction, showNativePip]);

  const hidePipWindow = useCallback(() => {
    if (isElectron) {
      hideNativePip();
      return;
    }

    if (pipWindowRef.current && !pipWindowRef.current.closed) {
      pipWindowRef.current.close();
      pipWindowRef.current = null;
    }
    setPipContainer(null);
    wasOpenedManuallyRef.current = false;
  }, [isElectron, hideNativePip]);

  const createPipWindow = useCallback(async () => {
    if (!enabled) {
      logPip('[PiP] PiP is disabled, cannot create window');
      return null;
    }

    // Prevent race condition from multiple rapid calls
    if (isCreatingPipRef.current) {
      logPip('[PiP] Already creating PiP window, skipping');
      return null;
    }

    try {
      isCreatingPipRef.current = true;

      if (!('documentPictureInPicture' in window)) {
        console.warn('Document Picture-in-Picture API is not available');
        return null;
      }

      const pipWindow = await (window as any).documentPictureInPicture.requestWindow({
        width: 240,
        height: 320,
        preferInitialWindowPlacement: false,
      });

      pipWindowRef.current = pipWindow;

      const pipDoc = pipWindow.document;

      for (const styleSheet of document.styleSheets) {
        try {
          const cssRules = [...styleSheet.cssRules].map((rule) => rule.cssText).join('');
          const style = pipDoc.createElement('style');
          style.textContent = cssRules;
          pipDoc.head.appendChild(style);
        } catch (_error) {
          const link = pipDoc.createElement('link');
          link.rel = 'stylesheet';
          link.href = styleSheet.href || '';
          if (styleSheet.href) {
            pipDoc.head.appendChild(link);
          }
        }
      }

      const container = pipDoc.createElement('div');
      container.id = 'pip-root';
      pipDoc.body.appendChild(container);

      setPipContainer(container);

      const handleVisibilityChange = () => {
        if (
          document.visibilityState === 'visible' &&
          pipWindow &&
          !pipWindow.closed &&
          !wasOpenedManuallyRef.current
        ) {
          hidePipWindow();
        }
      };

      document.addEventListener('visibilitychange', handleVisibilityChange);

      pipWindow.addEventListener('pagehide', () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        hidePipWindow();
      });

      return pipWindow;
    } catch (error) {
      console.error('Failed to create PiP window:', error);
      return null;
    } finally {
      isCreatingPipRef.current = false;
    }
  }, [enabled, hidePipWindow]);

  const openPipWindow = useCallback(async () => {
    if (!enabled) {
      logPip('[PiP] PiP is disabled, cannot open window');
      return;
    }

    if (isElectron) {
      showNativePip();
      return;
    }

    if (!pipWindowRef.current || pipWindowRef.current.closed) {
      logPip('[PiP] Manually opening PiP window');
      wasOpenedManuallyRef.current = true;
      await createPipWindow();
    } else {
      logPip('[PiP] PiP window already open, focusing it');
      pipWindowRef.current.focus();
    }
  }, [enabled, isElectron, showNativePip, createPipWindow]);

  // Web Media Session API (only for web, not Electron)
  useEffect(() => {
    if (isElectron) {
      return;
    }

    if (!enabled || !('mediaSession' in navigator)) {
      return;
    }

    const isActive =
      callStatus === 'in-progress' ||
      callStatus === 'ringing' ||
      callStatus === 'initiating' ||
      callStatus === 'transferring' ||
      callStatus === 'hangingup' ||
      callStatus === 'reconnectable';

    if (!isActive) {
      try {
        navigator.mediaSession.setActionHandler('enterpictureinpicture' as any, null);
        navigator.mediaSession.setActionHandler('togglemicrophone' as any, null);
        navigator.mediaSession.setActionHandler('hangup' as any, null);

        if ('setMicrophoneActive' in (navigator.mediaSession as any)) {
          (navigator.mediaSession as any).setMicrophoneActive(false);
        }
      } catch {
        // Ignore
      }
      return;
    }

    try {
      if ('setMicrophoneActive' in (navigator.mediaSession as any)) {
        (navigator.mediaSession as any).setMicrophoneActive(!isMuted);
      }
    } catch (e) {
      console.warn('[Auto-PiP] Failed to set microphone active state:', e);
    }

    try {
      navigator.mediaSession.setActionHandler(
        'enterpictureinpicture' as any,
        async ({ enterPictureInPictureReason }: any) => {
          console.info('[Auto-PiP] Handler called!', {
            enterPictureInPictureReason,
          });

          if (enterPictureInPictureReason === 'contentoccluded') {
            console.info('[Auto-PiP] Automatically entering picture-in-picture (content occluded)');
            wasOpenedManuallyRef.current = false;
          } else if (enterPictureInPictureReason === 'useraction') {
            console.info('[Auto-PiP] User action triggered PiP');
            wasOpenedManuallyRef.current = true;
          }

          if (!pipWindowRef.current || pipWindowRef.current.closed) {
            await createPipWindow();
          }
        },
      );
    } catch (error) {
      console.warn('[Auto-PiP] Auto-PiP not supported:', error);
    }

    try {
      navigator.mediaSession.setActionHandler('togglemicrophone' as any, () => {
        onToggleMute();
      });
    } catch (error) {
      console.warn('Toggle microphone action not supported:', error);
    }

    try {
      navigator.mediaSession.setActionHandler('hangup' as any, () => {
        onHangup();
      });
    } catch (error) {
      console.warn('Hangup action not supported:', error);
    }

    return () => {
      try {
        navigator.mediaSession.setActionHandler('enterpictureinpicture' as any, null);
        navigator.mediaSession.setActionHandler('togglemicrophone' as any, null);
        navigator.mediaSession.setActionHandler('hangup' as any, null);

        if ('setMicrophoneActive' in (navigator.mediaSession as any)) {
          (navigator.mediaSession as any).setMicrophoneActive(false);
        }
      } catch {
        // Ignore
      }
    };
  }, [
    isElectron,
    enabled,
    callStatus,
    isMuted,
    contact,
    direction,
    onToggleMute,
    onAnswer,
    onHangup,
    createPipWindow,
  ]);

  // Web: Sync microphone state
  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (isElectron) {
      return;
    }

    const isActive =
      callStatus === 'in-progress' ||
      callStatus === 'ringing' ||
      callStatus === 'initiating' ||
      callStatus === 'transferring' ||
      callStatus === 'hangingup' ||
      callStatus === 'reconnectable';
    if (isActive && 'setMicrophoneActive' in (navigator.mediaSession as any)) {
      try {
        (navigator.mediaSession as any).setMicrophoneActive(!isMuted);
      } catch {
        // Ignore
      }
    }
  }, [enabled, isElectron, isMuted, callStatus]);

  // Web: Close PiP when call ends
  useEffect(() => {
    if (isElectron) {
      return;
    }

    const isActive =
      callStatus === 'in-progress' ||
      callStatus === 'ringing' ||
      callStatus === 'initiating' ||
      callStatus === 'transferring' ||
      callStatus === 'hangingup' ||
      callStatus === 'reconnectable';

    // Don't close PiP during a power dialer session
    const hasActiveSession = !!getPipSessionData()?.isActive;

    if (!isActive && !hasSecondIncomingCall && !isDeclinedPip && !hasActiveSession) {
      hidePipWindow();
    }
  }, [isElectron, callStatus, hasSecondIncomingCall, isDeclinedPip, hidePipWindow]);

  // Auto-hide PiP after decline (voicemail state) with delay
  useEffect(() => {
    if (!isDeclinedPip) {
      return;
    }

    const timer = setTimeout(() => {
      setIsDeclinedPip(false);
      declinedSnapshotRef.current = null;
      if (isElectron) {
        destroyNativePip();
      } else {
        hidePipWindow();
      }
    }, DECLINED_AUTO_HIDE_DELAY);

    return () => clearTimeout(timer);
  }, [isDeclinedPip, isElectron, destroyNativePip, hidePipWindow]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isElectron) {
        destroyNativePip();
      } else {
        hidePipWindow();
      }
    };
  }, [isElectron, destroyNativePip, hidePipWindow]);

  // Web: Portal content for Document PiP
  const snapshot = isDeclinedPip ? declinedSnapshotRef.current : null;
  const webPipSessionData = getPipSessionData();
  const pipWin = pipWindowRef.current;

  const portalContent =
    !isElectron && pipContainer && pipWin
      ? createPortal(
          createElement(
            'div',
            {
              className: webPipSessionData?.isActive
                ? 'flex h-screen flex-col overflow-hidden'
                : 'flex h-screen flex-col overflow-hidden',
            },
            // Call UI — fixed at top, never scrolls
            createElement(
              'div',
              {
                className: webPipSessionData?.isActive
                  ? 'shrink-0'
                  : 'flex min-h-0 flex-1 flex-col',
              },
              createElement(CallPip, {
                className: webPipSessionData?.isActive ? 'h-[296px] flex-none' : 'min-h-0 flex-1',
                callId,
                contact: snapshot?.contact ?? contact,
                callStatus,
                isMuted,
                duration,
                direction,
                onToggleMute,
                onAnswer,
                onDecline: handleDeclineWithFlag,
                onHangup,
                onContactClick,
                showDeclinedState: isDeclinedPip,
                declineState,
                pipWindow: pipWin,
                userLine: snapshot?.userLine ?? userLine,
                extra: snapshot?.extra ?? extra,
                microphoneDeviceId,
                callTags: {
                  tags: callTagsList,
                  selectedTagKeys,
                  onAddTag: addTag,
                  onRemoveTag: removeTag,
                },
              }),
            ),
            // Session panel — fills remaining space, scrolls internally
            webPipSessionData?.isActive
              ? createElement(
                  'div',
                  { className: 'min-h-0 flex-1 overflow-hidden' },
                  createElement(PipSessionPanel, {
                    session: webPipSessionData,
                    onEndSession: pipEndSession,
                    onToggleList: (expanded) => {
                      pipWin.resizeTo(pipWin.innerWidth, expanded ? 780 : 456);
                    },
                    windowWidth: pipWin.innerWidth,
                  }),
                )
              : null,
          ),
          pipContainer,
        )
      : null;

  return {
    openPipWindow,
    hidePipWindow,
    isPipWindowOpen: isElectron ? isNativePipOpen : !!pipContainer,
    portalContent,
  };
}
